import { useCallback, useContext } from 'react';
import { SignalReviewContext } from './SignalReviewContext';
import type {
  SignalDefaults,
  ReviewableSignal,
} from '../../signal-review/types';

export function useSignalReview() {
  const context = useContext(SignalReviewContext);
  if (!context) {
    throw new Error(
      'useSignalReview must be used within a SignalReviewProvider'
    );
  }

  const { signals, setSignals } = context;

  const initializeSignals = useCallback(
    (initialSignals: ReviewableSignal[]) => {
      setSignals(initialSignals);
    },
    [setSignals]
  );

  const reinitializeSignals = useCallback(
    (
      currentDefaults: SignalDefaults,
      { resetExcluded = false }: { resetExcluded?: boolean } = {}
    ) => {
      setSignals((prev) =>
        prev.map((signal) => {
          const keepEdited = signal.edited && !resetExcluded;
          return {
            ...signal,
            ...(resetExcluded && { excluded: false, edited: false }),
            ...(keepEdited ? {} : currentDefaults),
          };
        })
      );
    },
    [setSignals]
  );

  const saveSignal = useCallback(
    (updated: ReviewableSignal) => {
      setSignals((prev) =>
        prev.map((signal) =>
          signal.index === updated.index ? updated : signal
        )
      );
    },
    [setSignals]
  );

  const toggleExclude = useCallback(
    (index: number) => {
      setSignals((prev) =>
        prev.map((signal) =>
          signal.index === index
            ? { ...signal, excluded: !signal.excluded }
            : signal
        )
      );
    },
    [setSignals]
  );

  const resetSignals = useCallback(() => {
    setSignals([]);
  }, [setSignals]);

  return {
    signals,
    initializeSignals,
    reinitializeSignals,
    saveSignal,
    toggleExclude,
    resetSignals,
  };
}
